import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { FileText, Plus, DollarSign, Send, Search, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Invoices() {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [bulkForm, setBulkForm] = useState({
    fee_structure_id: '',
    class_id: '',
    due_date: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash',
    bank_reference: ''
  });
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });

  useEffect(() => {
    fetchInvoices();
    fetchFeeStructures();
    fetchClasses();
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setInvoices(data);
    } catch (error) {
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeeStructures = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/fee-structures`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setFeeStructures(data.filter(s => s.is_active));
    } catch (error) {
      console.error('Failed to fetch fee structures');
    }
  };

  const fetchClasses = async () => {
    try {
      const res = await fetch(`${API_URL}/api/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(await res.json());
    } catch (error) {
      console.error('Failed to fetch classes');
    }
  };

  const handleBulkInvoice = async (e) => {
    e.preventDefault();

    if (!bulkForm.fee_structure_id) {
      toast.error('Please select a fee structure');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/finance/invoices/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          fee_structure_id: bulkForm.fee_structure_id,
          class_id: bulkForm.class_id || null,
          due_date: bulkForm.due_date || null
        })
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(`Created ${data.invoices_created} invoices. Total: KES ${data.total_amount_invoiced.toLocaleString()}`);
        setShowBulkDialog(false);
        fetchInvoices();
      } else {
        toast.error(data.detail || 'Bulk invoicing failed');
      }
    } catch (error) {
      toast.error('Failed to create bulk invoices');
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();

    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      const params = new URLSearchParams({
        amount: paymentForm.amount,
        payment_method: paymentForm.payment_method
      });
      if (paymentForm.bank_reference) {
        params.append('bank_reference', paymentForm.bank_reference);
      }

      const res = await fetch(`${API_URL}/api/finance/invoices/${selectedInvoice.id}/pay?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(`Payment recorded. Receipt: ${data.receipt_no}`);
        setShowPaymentDialog(false);
        setPaymentForm({ amount: '', payment_method: 'cash', bank_reference: '' });
        fetchInvoices();
      } else {
        toast.error(data.detail || 'Payment failed');
      }
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm({ amount: invoice.balance.toString(), payment_method: 'cash', bank_reference: '' });
    setShowPaymentDialog(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(amount);
  };

  const getStatusBadge = (status) => {
    const variants = {
      paid: 'bg-green-100 text-green-800',
      partial: 'bg-yellow-100 text-yellow-800',
      sent: 'bg-blue-100 text-blue-800',
      overdue: 'bg-red-100 text-red-800',
      draft: 'bg-slate-100 text-slate-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${variants[status] || variants.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesStatus = !filters.status || inv.status === filters.status;
    const matchesSearch = !filters.search || 
      inv.student_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      inv.invoice_no.toLowerCase().includes(filters.search.toLowerCase()) ||
      inv.student_admission_no?.toLowerCase().includes(filters.search.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const stats = {
    total: invoices.reduce((sum, inv) => sum + inv.total_amount, 0),
    paid: invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total_amount, 0),
    pending: invoices.reduce((sum, inv) => sum + inv.balance, 0)
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Student Invoices</h1>
          <p className="text-slate-500">Manage student fees and payments</p>
        </div>
        <Button onClick={() => setShowBulkDialog(true)} data-testid="bulk-invoice-btn">
          <Send className="w-4 h-4 mr-2" /> Bulk Invoice
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Invoiced</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.total)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Collected</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.paid)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Balance</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(stats.pending)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by student name, admission no, or invoice..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
              />
            </div>
            <Select
              value={filters.status || "all"}
              onValueChange={(value) => setFilters(prev => ({ ...prev, status: value === "all" ? "" : value }))}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Class</TableHead>
                <TableHead>Term</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvoices.map(invoice => (
                <TableRow key={invoice.id}>
                  <TableCell className="font-mono text-sm">{invoice.invoice_no}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{invoice.student_name}</p>
                      <p className="text-xs text-slate-500">{invoice.student_admission_no}</p>
                    </div>
                  </TableCell>
                  <TableCell>{invoice.class_name || '-'}</TableCell>
                  <TableCell>{invoice.term}</TableCell>
                  <TableCell className="text-right">{formatCurrency(invoice.total_amount)}</TableCell>
                  <TableCell className="text-right text-green-600">{formatCurrency(invoice.amount_paid)}</TableCell>
                  <TableCell className="text-right font-medium text-orange-600">{formatCurrency(invoice.balance)}</TableCell>
                  <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                  <TableCell>
                    {invoice.balance > 0 && (
                      <Button size="sm" variant="outline" onClick={() => openPaymentDialog(invoice)}>
                        <DollarSign className="w-3 h-3 mr-1" /> Pay
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {filteredInvoices.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No invoices found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Invoice Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Invoice Students</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBulkInvoice} className="space-y-4">
            <div>
              <Label>Fee Structure *</Label>
              <Select
                value={bulkForm.fee_structure_id}
                onValueChange={(value) => setBulkForm(prev => ({ ...prev, fee_structure_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select fee structure" />
                </SelectTrigger>
                <SelectContent>
                  {feeStructures.map(fs => (
                    <SelectItem key={fs.id} value={fs.id}>
                      {fs.name} - {formatCurrency(fs.total_amount)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Filter by Class (Optional)</Label>
              <Select
                value={bulkForm.class_id || "all"}
                onValueChange={(value) => setBulkForm(prev => ({ ...prev, class_id: value === "all" ? "" : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Classes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classes</SelectItem>
                  {classes.map(cls => (
                    <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={bulkForm.due_date}
                onChange={(e) => setBulkForm(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowBulkDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-bulk-invoice-btn">Generate Invoices</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <form onSubmit={handleRecordPayment} className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Invoice:</span>
                  <span className="font-mono">{selectedInvoice.invoice_no}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Student:</span>
                  <span>{selectedInvoice.student_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Balance Due:</span>
                  <span className="font-bold text-orange-600">{formatCurrency(selectedInvoice.balance)}</span>
                </div>
              </div>

              <div>
                <Label>Payment Amount *</Label>
                <Input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                  max={selectedInvoice.balance}
                  step="0.01"
                  required
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select
                  value={paymentForm.payment_method}
                  onValueChange={(value) => setPaymentForm(prev => ({ ...prev, payment_method: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="mpesa">M-Pesa</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reference (Optional)</Label>
                <Input
                  value={paymentForm.bank_reference}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, bank_reference: e.target.value }))}
                  placeholder="Transaction reference or cheque number"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
                <Button type="submit" data-testid="submit-payment-btn">Record Payment</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
